import React, { useState, useEffect, useCallback } from 'react';
import { fetchAllProjectProblems, getAnalysisStatusDisplay, getReviewStatusDisplay } from './ocrApi';
import { needsRereview, cellStyle } from './analysisCell';

/**
 * 분석 지도 (⑤ AI 분석 단계, 진행률 카드 바로 아래).
 *
 * 진행률 카드는 「완료 16 / 22건」처럼 숫자만 말해서, 남은 6건이 **어느 문제인지** 알 수 없었다.
 * 인덱스 지도(ProblemNumberMap)가 검수 상태로 하는 일을 여기서는 분석 상태로 한다 —
 * 칸 하나가 문제 하나이고, 누르면 그 문제가 검수 모달로 열린다.
 *
 * 칸 모양·크기·클릭 동작·범례 형식은 인덱스 지도의 값을 그대로 쓴다. 두 지도가 한 화면에서
 * 다른 얼굴이면 같은 격자를 다른 것으로 읽게 된다.
 * 색은 뜻이 같은 자리에 같은 값을 쓴다 — 「완료」는 인덱스 지도의 검수완료와 같은 초록,
 * 「미실행」은 검수대기와 같은 흰 칸이다.
 *
 * 목록 응답만 쓴다(analysis_status + review_status). 전량이 필요한 이유는 진행률 카드가 세는
 * 대상이 프로젝트 전체이기 때문이다 — 20건씩 넘겨 보는 목록으로는 남은 것을 짚을 수 없다.
 *
 * **두 값을 함께 읽는 이유**: ⑤의 완료 조건이 둘이다. 백엔드 계약이 그렇게 정의한다
 * (ocr-pipeline-stage-model-backend-handoff.html §4.5) —
 *   review_pending_count   = review_status 100 이고 analysis_status ≠ 200 (원본 미검수, ④의 조건)
 *   rereview_pending_count = review_status 100 이고 analysis_status = 200 (AI가 되돌려 보낸 것, ⑤의 조건)
 * 즉 「AI가 끝냈나」와 「사람이 그 결과를 봤나」는 다른 축이고, ⑤가 끝나려면 둘 다여야 한다.
 * 종전에는 analysis_status 하나만 그려 **분석이 끝나 되돌아온 문제와 승인까지 끝난 문제가 같은
 * 초록**이었다. 같은 단계의 배지는 「재검수 대기 N건」이라 말하는데 지도가 그 N건을 못 짚었다 —
 * 이 지도가 만들어진 이유(「남은 6건이 어느 문제인지 알 수 없었다」)와 같은 결함이다.
 */

const GRID_CAP = 500; // 이보다 많으면 지도가 아니라 벽이 된다 — 요약으로 강등(인덱스 지도와 같은 기준)

function AnalysisMap({ projectId, reloadKey, onOpenProblem, getAccessToken, className = '' }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const d = await fetchAllProjectProblems(projectId, getAccessToken);
      setItems(d.items);
    } catch (e) {
      // 갱신 실패에 격자를 지우지 않는다 — 분석이 도는 동안 5초마다 다시 읽으므로 한 번 흔들린
      // 조회 때문에 지도가 사라지면 화면이 깜빡인다. 마지막으로 읽은 상태를 남기고, 그것이
      // 마지막이라는 사실은 범례 아래 한 줄이 말한다(조용히 옛 값을 보여 주지 않는다).
      setError(e.message || '분석 지도를 만들지 못했습니다.');
    } finally {
      setLoading(false);
    }
  }, [projectId, getAccessToken]);

  useEffect(() => { load(); }, [load, reloadKey]);

  // 「읽는 중」은 처음 한 번만 자리를 차지한다. 분석이 도는 동안 reloadKey가 계속 바뀌는데
  // 매번 격자를 걷어내면 5초마다 화면이 비었다 돌아온다 — 갱신 중에는 직전 격자를 그대로 두고
  // 새 값이 도착하면 조용히 갈아끼운다.
  if (loading && items.length === 0) return <p className={`text-xs text-gray-400 ${className}`}>분석 상태를 읽는 중…</p>;
  if (error && items.length === 0) return <p className={`text-xs text-danger ${className}`}>{error}</p>;
  if (items.length === 0) return null;

  // 격자를 그리든 요약으로 강등하든 같은 축으로 센다 — 크기가 달라졌다고 뜻이 달라지면 안 된다.
  const rereview = items.filter(needsRereview).length;
  const reviewed = items.filter((p) => p.analysis_status === 200 && p.review_status === 200).length;
  const failed = items.filter((p) => p.analysis_status === 500).length;

  if (items.length > GRID_CAP) {
    return (
      <p className={`text-xs text-gray-500 ${className}`}>
        문제가 {items.length}건이라 칸으로 그리지 않습니다 — 검수 완료 {reviewed}건
        {rereview > 0 && ` · 재검수 대기 ${rereview}건`}
        {failed > 0 && ` · 실패 ${failed}건`}
      </p>
    );
  }

  return (
    <div className={`space-y-2 ${className}`}>
      <div className="flex flex-wrap gap-1">
        {items.map((p, i) => {
          const status = p.analysis_status ?? null;
          const info = status != null ? getAnalysisStatusDisplay(status) : null;
          const n = p.problem_number ?? i + 1;
          const canOpen = !!onOpenProblem;
          // 툴팁은 두 축을 다 말한다 — 색이 둘을 합쳐 한 칸으로 그리므로, 칸이 뭉갠 것을 여기서 편다.
          // 색을 구분하지 못해도 상태를 읽을 수 있어야 한다(범례와 같은 이유).
          const reviewInfo = p.review_status != null ? getReviewStatusDisplay(p.review_status) : null;
          const reviewText = needsRereview(p) && p.review_status === 100 ? '재검수 대기' : reviewInfo?.text;
          return (
            <button
              key={p.ocr_prob_id}
              type="button"
              onClick={canOpen ? () => onOpenProblem(p.ocr_prob_id, i) : undefined}
              disabled={!canOpen}
              title={`${n}번 · ${info ? info.text : '분석 상태 모름'}${reviewText ? ` · ${reviewText}` : ''}${canOpen ? ' — 눌러서 열기' : ''}`}
              className={`h-7 min-w-7 rounded-md border px-1 text-[10px] leading-none transition-colors ${cellStyle(status, needsRereview(p))} ${
                canOpen ? 'cursor-pointer hover:brightness-95' : 'cursor-default'
              }`}
            >
              {n}
            </button>
          );
        })}
      </div>
      <div className="flex flex-wrap items-center gap-3 text-[10px] text-gray-400">
        <span className="flex items-center gap-1">
          <span className="inline-block size-3 rounded border border-green-300 bg-green-100" /> 검수 완료
        </span>
        <span className="flex items-center gap-1">
          <span className="inline-block size-3 rounded border border-amber-300 bg-amber-100" /> 재검수 대기
        </span>
        <span className="flex items-center gap-1">
          <span className="inline-block size-3 rounded border border-blue-300 bg-blue-100" /> 분석중
        </span>
        <span className="flex items-center gap-1">
          <span className="inline-block size-3 rounded border border-gray-300 bg-gray-100" /> 분석대기
        </span>
        <span className="flex items-center gap-1">
          <span className="inline-block size-3 rounded border border-gray-200 bg-white" /> 분석 미실행
        </span>
        {failed > 0 && (
          <span className="flex items-center gap-1">
            <span className="inline-block size-3 rounded border border-red-400 bg-red-100" /> 분석실패
          </span>
        )}
      </div>
      {/* 격자를 남긴 채 실패했을 때만 뜬다. 다음 갱신이 성공하면 사라지므로 눌러앉는 경고가 아니다. */}
      {error && (
        <p className="text-[10px] text-amber-600">갱신하지 못했습니다 — 위 격자는 마지막으로 읽은 상태입니다.</p>
      )}
    </div>
  );
}

export default AnalysisMap;
